import { useAppTranslation } from "@/i18n/useAppTranslation";
import { BRAND_GREEN } from "@/lib/design-tokens";
import { ApexLogo } from "@/components/brand/ApexLogo";
import { marketingImage } from "@/lib/site-assets";

// Celebratory classic-car backdrop for the finish header (the same R2-hosted
// hero used on the Pro Owner page) tinted with a brand-green gradient overlay,
// replacing the flat green fill while keeping the APEX mark and title legible.
const FINISH_HERO = marketingImage("1712338354_phpU6JPH0_1775238106298.jpg");

interface HostTourFinishModalProps {
  open: boolean;
  onClose: () => void;
  apexEnabled?: boolean;
  // Task #4221 — reused by the Earnings & Reviews tours. Callers override the
  // headline/body copy; defaults preserve the original Dashboard tour keys.
  // The APEX upsell variant is Dashboard-only, so the new tours simply omit
  // `apexEnabled` and get the plain 🎉 + body layout.
  titleKey?: string;
  bodyKey?: string;
}

export function HostTourFinishModal({
  open,
  onClose,
  apexEnabled,
  titleKey = "host_tour.finish_title",
  bodyKey = "host_tour.finish_body",
}: HostTourFinishModalProps) {
  const { t } = useAppTranslation();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 material-scrim animate-in fade-in-0 duration-300"
        onClick={onClose}
      />

      <div className="relative z-[101] mx-4 w-full max-w-lg animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-300">
        <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="relative text-center">
            <div className="relative flex h-48 w-full items-center justify-center overflow-hidden sm:h-56">
              <picture>
                <source type="image/webp" srcSet={FINISH_HERO.webp} />
                <img
                  src={FINISH_HERO.fallback}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </picture>

              {/* Brand-green wash over the photo: strong toward the top so the
                  white APEX mark + title stay legible, lighter toward the lower
                  corner so the classic car still reads through. */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand/90 via-brand/70 to-brand-light/55" />

              {apexEnabled && (
                <div className="relative flex flex-col items-center gap-3">
                  {/* material: intentional — decorative frosted circle sits on an opaque brand-green gradient, not a floating surface */}
                  <div className="flex h-28 w-28 items-center justify-center rounded-full border-2 border-white/40 bg-white/10 backdrop-blur-sm">
                    <ApexLogo size={64} className="h-16 w-16" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/80">DRIVESHARE APEX</span>
                </div>
              )}

              {!apexEnabled && (
                <div className="relative flex flex-col items-center gap-2">
                  <span className="text-6xl drop-shadow-lg">🎉</span>
                </div>
              )}

              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </div>

            <div className="absolute inset-x-0 bottom-0 px-8 pb-5">
              <h2 className="font-sans text-2xl font-bold text-white drop-shadow-lg sm:text-3xl">
                {t(titleKey)}
              </h2>
            </div>
          </div>

          <div className="px-8 pb-8 pt-6">
            {apexEnabled ? (
              <div className="space-y-4">
                <p className="text-sm leading-relaxed text-gray-600">
                  {t("host_tour.finish_body_apex")}
                </p>
                <div className="rounded-xl border border-border bg-muted/30 p-4">
                  <div className="flex items-start gap-3">
                    <ApexLogo size={20} className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{t("host_tour.apex_explainer_title")}</p>
                      <p className="mt-1 text-xs leading-relaxed text-gray-500">
                        {t("host_tour.apex_explainer_body")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm leading-relaxed text-gray-600">
                {t(bodyKey)}
              </p>
            )}

            <button
              type="button"
              onClick={onClose}
              className="mt-6 flex w-full items-center justify-center rounded-lg px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90"
              style={{ backgroundColor: BRAND_GREEN }}
            >
              {t("host_tour.finish_cta")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
